import { GridSingleSelectColDef } from '@mui/x-data-grid-pro';
import { capitalizeText } from '../../../utils/helpers';
import { format } from 'date-fns';
import { standardOperators } from '../../Compounds/UpstackDataGrid/helpers';
import { TagColorRenderer } from '../UpstackDataGrid/CellRenderers/TagColorRenderer';
import { removeTag, Tag, TagRelationSummary } from '../../../store/Tags';
import { TagNameRenderer } from '../UpstackDataGrid/CellRenderers/TagNameRenderer';
import { DeleteBtn } from '../DeleteBtn';

const relationFormatter = (row: Tag) => {
  const relationBreakdown: Record<string, number> = (row.relations || []).reduce(
    (acc: Record<string, number>, relation: TagRelationSummary) => {
      if (!acc[relation.entityType]) acc[relation.entityType] = 0;
      acc[relation.entityType]++;
      return acc;
    },
    {}
  );
  const relationStrings = Object.entries(relationBreakdown).map(
    ([entityType, count]) => `${count} ${capitalizeText(entityType)}${count > 1 ? 's' : ''}`
  );
  return relationStrings.length > 0 ? relationStrings.join(', ') : 'No Relations';
};

// Typed on the row so the narrowed tag payload is load-bearing: a column reading a field the api no longer serves fails the build.
export const DefaultTagGridColumnDefs: GridSingleSelectColDef<Tag>[] = [
  {
    field: 'name',
    headerName: 'Tag Name',
    sortable: true,
    flex: 2,
    renderCell: (params) => {
      return <TagNameRenderer tag={params.row} />;
    },
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    filterOperators: standardOperators(),
    description: 'Drag to move column',
    cellClassName: 'mui-grid-cell-interactive'
  },
  {
    field: 'color',
    headerName: 'Color',
    sortable: true,
    flex: 1,
    renderCell: (params) => {
      return <TagColorRenderer tag={params.row} />;
    },
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    filterOperators: standardOperators(),
    description: 'Drag to move column'
  },
  {
    field: 'relations',
    headerName: '# of Associated Relations',
    sortable: true,
    flex: 3,
    type: 'singleSelect',
    valueOptions: [],
    renderCell: (params) => {
      const relationString = relationFormatter(params.row);
      return <div>{relationString}</div>;
    },
    valueFormatter: (value, row) => relationFormatter(row),
    valueGetter: (value, row) => relationFormatter(row),
    disableReorder: true,
    filterable: false,
    filterOperators: standardOperators()
  },
  {
    field: 'updated_at',
    headerName: 'Last Modified Date',
    sortable: true,
    valueFormatter: (value) => {
      const date = new Date(value);
      return value ? format(new Date(date.getTime() + date.getTimezoneOffset()), 'MM/dd/yyyy HH:mm:ss') : '';
    },
    valueGetter: (value) => {
      const date = new Date(value);
      return value ? format(date, 'MM/dd/yyyy HH:mm:ss') : '';
    },
    flex: 2,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    filterOperators: standardOperators(),
    description: 'Drag to move column'
  },
  {
    field: 'updatedBy',
    headerName: 'Last Modified By',
    sortable: true,
    valueGetter: (value, row) => row.updatedBy?.name || 'Unknown',
    valueFormatter: (value) => value || 'Unknown',
    flex: 2,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    filterOperators: standardOperators(),
    description: 'Drag to move column'
  },
  {
    field: 'actions',
    headerName: 'Actions',
    flex: 1,
    filterable: false,
    sortable: false,
    type: 'singleSelect',
    valueOptions: [],
    filterOperators: standardOperators(),
    renderCell: (params) => (
      <div className="flex justify-center items-center h-full">
        <DeleteBtn
          copyMap={{
            default:
              'Are you sure you want to permanently delete this tag? Deletion will remove tag from all associated records within the Portal.',
            deleting: 'Deleting tag...',
            error: 'Something went wrong while deleting this tag. Please try again'
          }}
          deleteTriggerDescription={`Delete tag: ${params.row.name}`}
          deleteItemId={params.row.id}
          deleteItem={() => removeTag(params.row.id, true, params.row.accountId)}
        />
      </div>
    ),
    description: 'Drag to move column'
  }
];
